import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import type { EmployeeRole } from '../../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: (EmployeeRole | 'SuperAdmin')[];
}

/** Where users with a server-assigned password are held until they choose their own. */
export const FORCE_PASSWORD_CHANGE_PATH = '/change-password';


export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, employee, isSuperAdmin } = useAuthStore();
  const location = useLocation();

  // Not authenticated - redirect to login
  if (!isAuthenticated || !employee) {
    // Speichere die aktuelle URL für Redirect nach Login
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Account still carries a password the user did not choose (Excel import, admin reset).
  // Nothing else is reachable until they pick their own — checked before the role gate so
  // a user without access to the target page still lands on the change screen, not on
  // /unauthorized.
  if (employee.mustChangePassword && location.pathname !== FORCE_PASSWORD_CHANGE_PATH) {
    return <Navigate to={FORCE_PASSWORD_CHANGE_PATH} replace />;
  }

  // Check role-based access
  if (allowedRoles) {
    const hasAccess = 
      (isSuperAdmin && allowedRoles.includes('SuperAdmin')) ||
      (employee.role && allowedRoles.includes(employee.role as EmployeeRole));
    
    if (!hasAccess) {
      // User doesn't have permission - redirect to unauthorized or home
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}
